import { detectBreakpoints } from "../detectBreakpoints";
import { useFrameDocument } from "../utils/useFrameDocument";
import { useChatRoomContext } from "./useChatRoomContext";

export const calculateScrollInfo = ({
  event,
  scrollerId = "message-scroll-view",
  targetDocument,
}: {
  event: string;
  scrollerId?: string;
  targetDocument?: Document;
}) => {
  const { isMobileView } = detectBreakpoints();
  const doc = targetDocument || document;

  const element = doc.getElementById(scrollerId);
  if (!element) return {};

  let action = "noScroll";

  switch (event) {
    case "messageSent":
      action = "scrollToBottom";
      break;
    case "messageReceived": {
      const distanceFromBottom =
        element.scrollTop - element.scrollHeight + element.offsetHeight;

      const isMobileBottom = isMobileView() && distanceFromBottom === 0;
      const isBottomRange =
        distanceFromBottom >= -(element.offsetHeight * 0.8) ||
        distanceFromBottom > 14;

      if (isMobileBottom || isBottomRange) action = "scrollToBottom";
      break;
    }
    case "chatRoomLoad":
      action = "scrollToUnreadMessageIndicator";
      break;
    case "paginatedMessageLoad":
      action = "adjustTop";
      break;
    case "nextPaginatedMessageLoad":
    case "messageUpdated":
    case "messageDeleted":
    case "parentMessageUpdated":
      // action remains "noScroll" (default value)
      break;
    case "reaction": {
      const distanceFromBottom =
        element.scrollTop - element.scrollHeight + element.offsetHeight;
      const isCloseToTheBottom = distanceFromBottom >= -80;
      if (isCloseToTheBottom) action = "scrollToBottom";
      break;
    }
  }
  return {
    action: action,
    scrollTopValue: element.scrollTop,
    scrollHeightValue: element.scrollHeight,
  };
};

export const getScrollerId = (isMessaging: boolean, chatRoomUuid: string) =>
  isMessaging && chatRoomUuid
    ? `message-scroll-view-${chatRoomUuid}`
    : "message-scroll-view";

export const useScrollInfo = () => {
  const { chatRoomUuid, isMessaging } = useChatRoomContext();
  const { frameDocument } = useFrameDocument();
  const scrollerId = getScrollerId(isMessaging, chatRoomUuid);
  const targetDocument = frameDocument || document;

  const getScrollingElement = () => targetDocument.getElementById(scrollerId);

  const calculateScrollInfoWithScrollerId = ({ event }: { event: string }) =>
    calculateScrollInfo({ event, scrollerId, targetDocument });

  return {
    calculateScrollInfo: calculateScrollInfoWithScrollerId,
    scrollerId,
    getScrollingElement,
    targetDocument,
  };
};
